import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Container,
  TextField,
  Typography,
  LinearProgress,
  Box,
  Paper,
  Button,
  Stack,
  Divider,
  Autocomplete,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  FormGroup,
  RadioGroup,
  Radio,
  InputAdornment,
  Dialog,
  Slide,
  DialogTitle,
  DialogContent,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  DialogActions,
  CircularProgress,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Tooltip,
  Snackbar,
  Alert,
  ThemeProvider,
  Fade,
  Grow,
  Zoom,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, gql, useMutation, useLazyQuery } from "@apollo/client";
import { Helmet } from "react-helmet-async";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import countries from "./countries.json";
import { IMAGES_API_URL } from "./constants";
import LogoPng from "./Assets/eventegration.png";
import { getBranding } from "./utils/branding";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import SearchModal from "./Components/SearchModal";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery } from "@mui/material";
import { useFeatures } from "./Hooks/GetFeatures";
import { usePaymentDetail } from "./Hooks/GetPaymentDetail";
import { useScreenSize } from "./Hooks/useScreenSize";
import ScreenSizeDebugger from "./Components/ScreenSizeDebugger";
import PaypalBtns from "./PaymentFolder/Paypal";
import PaymobBtns from "./PaymentFolder/Paymob";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GppBadIcon from "@mui/icons-material/GppBad";
import Turnstile, { useTurnstile } from "react-turnstile";
import LinkNotFound from "./Components/LinkNotFound";
import useGeoLocation from "react-ipgeolocation";
import CustomRadioButton from "./Components/CustomRadioButton";
import EventClosed from "./Components/EventClosed";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import useCheckEmail from "./Hooks/useCheckEmail";
import useCheckMobile from "./Hooks/useCheckMobile";
import OtpModal from "./Components/OtpModal";
import CheckoutBtns from "./PaymentFolder/CheckoutBtns";
import CardInput from "./PaymentFolder/CardInput";
import { FiCheckCircle } from "react-icons/fi";
import { AttachMoney, Discount, Info, LocalOffer } from "@mui/icons-material";
import ShareDialog from "./Components/ShareDialog";
import FormField from "./Components/FormField";
import PaymentSummary from "./Components/PaymentSummary";
import FormProgress from "./Components/FormProgress";
import FormSummary from "./Components/FormSummary";
import PaymentSummaryDialog from "./Components/PaymentSummaryDialog";
import SocialRegistrationChoice from "./Components/SocialRegistrationChoice";
import SocialRegistrationForm from "./Components/SocialRegistrationForm";
import EmailVerificationStep from "./Components/EmailVerificationStep";
import { createCustomTheme, parseTheme } from "./utils/themeUtils";
import { useSocialAuth } from "./Hooks/useSocialAuth";
import LoadingState from "./Components/LoadingState";
import InvitationRegistrationForm from "./Components/InvitationRegistrationForm";
import StatusScreen from "./Components/StatusScreen";
import {
  extractReferralParams,
  extractRegLinkSource,
  initializeReferralTracking,
  prepareReferralFormData,
  getReferralLink,
} from "./utils/referralUtils";
import {
  getGraphQLErrorMessage,
  isGraphQLError,
  analyzeGraphQLError,
} from "./utils/apiUtils";

export const DECRYPT_PMT = gql`
  query DecryptPmt($encryptedPmt: String!) {
    decryptPmt(encryptedPmt: $encryptedPmt)
  }
`;

export const CHECK_MEMBER = gql`
  query CheckMemberId($memberId: String!, $link: String!) {
    checkMemberId(memberId: $memberId, link: $link) {
      isValid
      message
      discount
    }
  }
`;

// Promo Code validation query. `email` is optional but REQUIRED for individual
// (fixed-email) coupons — the backend uses it to enforce the allow-list; without it
// an individual coupon can never validate.
export const VALIDATE_PROMO = gql`
  query ValidatePromo($code: String!, $link: String!, $email: String) {
    validatePromo(code: $code, link: $link, email: $email) {
      status
      discountValue
      discountPercent
      message
    }
  }
`;

// Add missing GraphQL queries
export const GET_REG_STRUCTURE = gql`
  query ($link: String!, $uid: String) {
    reg(link: $link, uid: $uid) {
      eventId
      eventName
      role
      formHeading
      headerMode
      headerImageUrl
      onlyPmt
      shareContent
      theme
      isInvitation
      invitationData
      capacity
      fields {
        name
        type
        label
        sx
        xs
        md
        placeholder
        options
        defaultValue
        minLength
        maxLength
        pattern
        required
        readonly
      }
    }
  }
`;

export const REGISTER = gql`
  mutation ($link: String!, $jsonData: String!) {
    register(link: $link, jsonData: $jsonData)
  }
`;

export const PAYMENT_REGISTER = gql`
  mutation ($link: String!, $jsonData: String!, $transaction: String!) {
    paymentRegister(link: $link, jsonData: $jsonData, transaction: $transaction)
  }
`;

// Form field validation builder
export const buildValidation = (f, uid = false) => {
  let v = yup;
  if (f.type === "checkbox") {
    v = v.boolean();
  } else if (f.type === "select") {
    v = v.string();
  } else if (f.type === "mcq") {
    if (f.required) {
      return v.mixed().test({
        message: "Required",
        test: (v) => {
          return v?.length > 0;
        },
      });
    } else {
      return v.array().of(yup.string());
    }
  } else if (f.type === "radio" || f.type === "payment") {
    v = v.string();
  } else if (f.type === "number") {
    v = v.number();
  } else if (f.type === "email") {
    v = v.string().trim().email();
  } else if (f.type === "confirm_email") {
    v = v
      .string()
      .trim()
      .test({
        message: "Does not match",
        test: (val, p) => {
          if ("Email Address" in p.parent) {
            return val === p.parent["Email Address"];
          }
          return val === p.parent["Email"];
        },
      });
  } else {
    v = v.string().trim();
  }

  // For UID links, make text/email fields non-mandatory
  const isRequired =
    f.required &&
    !(
      uid &&
      (f.type === "text" ||
        f.type === "email" ||
        f.type === "number" ||
        f.type === "confirm_email" ||
        (f.type === undefined &&
          ![
            "checkbox",
            "radio",
            "payment",
            "select",
            "mcq",
            "country",
            "mobile_code",
            "calling_code",
            "permission",
            "html",
          ].includes(f.type)))
    );
  console.warn("isRequired", isRequired);
  if (isRequired) {
    v = v.required("Required");
  }

  if (
    f.maxLength &&
    !["checkbox", "radio", "payment", "select", "mcq"].includes(f.type)
  ) {
    v = v.max(
      f.maxLength,
      f.type === "number"
        ? `Max value: ${f.maxLength}`
        : `Max ${f.maxLength} characters allowed`,
    );
  }
  if (
    f.minLength &&
    !["checkbox", "radio", "payment", "select", "mcq"].includes(f.type)
  ) {
    v = v.min(
      f.minLength,
      f.type === "number"
        ? `Min value: ${f.minLength}`
        : `Min ${f.minLength} characters required`,
    );
  }
  if (f.pattern) {
    v = v.matches(f.pattern, "Invalid pattern!");
  }
  return v;
};

// Helper function to determine what text to display based on language features
const getLanguageDisplay = (featuresList, arabicText, englishText) => {
  const hasArabic = featuresList.includes("arabic");
  const hasEnglish = featuresList.includes("english");

  // If arabic-only (has arabic but not english)
  if (hasArabic && !hasEnglish) {
    return { showArabic: true, showEnglish: false };
  }

  // If english-only (has english but not arabic)
  if (hasEnglish && !hasArabic) {
    return { showArabic: false, showEnglish: true };
  }

  // If both are enabled, or neither is specified (default to both)
  return { showArabic: true, showEnglish: true };
};

export default function RegistrationPage() {
  const { link } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const chkmid = queryParams.get("chkmid");
  const iplink = queryParams.get("iplink");
  const uid = queryParams.get("uid"); // Extract UID parameter for invitations

  // Owner 2026-09-12: a registration link whose slug carries "iframe" (e.g.
  // "ofsec-visitor-iframe-2026") is meant to be EMBEDDED on the client's own site, so it must
  // render the same chrome-less UI as the dedicated /iframe/:link page (RegistrationIframe.jsx)
  // — i.e. WITHOUT the event banner, because the host page already shows its own branding.
  // Slug-based on purpose: these links are pasted into a client's <iframe src> and are also
  // opened directly in a tab for QA, and both cases must look the same.
  const isEmbed = /iframe/i.test(link || "");

  // Extract referral parameters
  const referralParams = extractReferralParams();

  // Get referral link if referral parameters are present
  const registrationLink = referralParams.hasReferralParams
    ? getReferralLink(link, referralParams)
    : link;

  // Debug logging for URL parameters
  console.log("🔍 URL Search Params:", location.search);
  console.log("🔍 UID from URL:", uid);
  console.log(
    "🔍 All query params:",
    Object.fromEntries(queryParams.entries()),
  );

  const { data, loading, error } = useQuery(GET_REG_STRUCTURE, {
    variables: { link: registrationLink, uid },
    skip: !link,
  });

  // Social Media Registration State
  const [registrationMode, setRegistrationMode] = useState("choice"); // 'choice', 'social', 'manual'
  const [selectedProvider, setSelectedProvider] = useState(null);

  // Snackbar state for modern notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error", // 'error', 'warning', 'info', 'success'
  });

  // Initialize referral tracking
  React.useEffect(() => {
    initializeReferralTracking(setSnackbar);
  }, []);

  // Theme handling
  const customTheme = createCustomTheme(data?.reg?.theme);
  const parsedTheme = parseTheme(data?.reg?.theme) || {
    primary: "#000000",
    secondary: "#0066cc",
    background: "#ffffff",
    surface: "#f5f5f5",
  };

  console.log("Value of chkmid:", chkmid); // This will log "true" based on the given URL.
  const searchParams = new URLSearchParams(window.location.search);
  // console.log(searchParams);
  const encryptedPmt = searchParams.get("pmt");
  console.log(chkmid, link);
  // console.log(">>>", encryptedPmt);

  const {
    data: pmtData,
    loading: pmtLoading,
    error: pmtError,
  } = useQuery(DECRYPT_PMT, {
    variables: { encryptedPmt },
    skip: !encryptedPmt,
  });

  const pmt = pmtData?.decryptPmt ? JSON.parse(pmtData.decryptPmt) : null;

  React.useEffect(() => {
    if (pmt && !pmt.isExhausted) {
      console.log("Decrypted PMT:", pmt);
      // return(
      //   <div>
      //     <p>Link Exhausted</p>
      //   </div>
      // )
    }
  }, [pmt]);

  const [anchorEl, setAnchorEl] = React.useState(null);
  // Add state for share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // SPE: after a successful registration, if THIS role's Share Generator is enabled, offer a CTA popup
  // that opens the Share Generator (the "I'm attending" card). Fails soft — no popup if disabled/absent.
  const [sgCtaOpen, setSgCtaOpen] = useState(false);
  const [sgConfig, setSgConfig] = useState(null);
  // The email the visitor just registered with — passed to the Share Generator as ?prefill_email=<email>
  // so the "I'm attending" card auto-fills their own name/company (the register mutation returns only a
  // message, not the new id, so we identify the row by email instead).
  const [sgRegEmail, setSgRegEmail] = useState("");

  // Open the Share Generator for this event/role. Shared by BOTH the post-registration popup and the
  // persistent share button on the response page (FormSummary), so the share entry point survives dismissing
  // the popup. Extracted verbatim from the popup's original onClick so behaviour is unchanged.
  const openShareGenerator = () => {
    const evId = data?.reg?.eventId;
    const evRole = data?.reg?.role;
    // The Share Generator is served on the canonical www host. Use the current origin ONLY when it is clearly
    // the production www app; otherwise (localhost / staging / preview / an iframe host) fall back to the
    // canonical URL so the CTA never opens a 404.
    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    const isProdWww = /^https:\/\/(www\.)?eventegration\.com$/.test(origin);
    const base = isProdWww ? `${origin}/share` : "https://eventegration.com/share";
    let url = `${base}/${evId}${evRole ? `/${encodeURIComponent(evRole)}` : ""}`;
    // Carry the just-registered email so the share card auto-fills the sharer's own name/company. Uses
    // prefill_email (not ?prefill=<id>) because the register mutation returns only a message, not the id.
    if (sgRegEmail) {
      url += `?prefill_email=${encodeURIComponent(sgRegEmail)}`;
    }
    // Try a new tab; if the browser blocks it (common inside sandboxed iframes), navigate the top window so
    // the button never silently does nothing.
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      try {
        (window.top || window).location.href = url;
      } catch {
        window.location.href = url;
      }
    }
    setSgCtaOpen(false);
  };
  const [checkShareGenerator] = useLazyQuery(
    gql`
      query SgEnabled($eventId: String!, $role: String) {
        shareGeneratorConfig(eventId: $eventId, role: $role) {
          enabled
          shareTitle
        }
      }
    `,
    { fetchPolicy: "network-only" },
  );

  const handleShareClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  // console.log(data?.reg, error?.message);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const responseBoxRef = React.useRef(null);
  // console.log('eventID',data?.reg?.eventId);

  const { featureData, featureLoading } = useFeatures(
    data?.reg?.eventId,
    data?.reg?.role,
  );

  // Social Media Registration Hook
  const socialAuth = useSocialAuth(link);

  // Social registration now handled by the useSocialAuth hook

  // Email check query for social registration
  const [checkEmailMutation] = useLazyQuery(gql`
    query CheckEmail($email: String!, $link: String!) {
      checkEmail(email: $email, link: $link)
    }
  `);

  const [response, setResponse] = React.useState("");

  // SPE: when a registration succeeds (a non-rejection response appears), check if this role's Share
  // Generator is enabled and, if so, pop the share CTA. Fires once per success; fully best-effort.
  React.useEffect(() => {
    // Only offer the "share you're attending" CTA on a genuine REGISTRATION success — never on a declined
    // invitation, a rejection, or a pending/under-review message (those must not be congratulated).
    const r = String(response || "").toLowerCase();
    const looksNegative =
      !response ||
      r.includes("invitation status") ||
      r.includes("declined") ||
      r.includes("decline the invitation") ||
      r.includes("rejected") ||
      r.includes("not approved") ||
      r.includes("under review") ||
      r.includes("pending") ||
      r.includes("could not") ||
      r.includes("failed");
    if (looksNegative) return;
    const evId = data?.reg?.eventId;
    const evRole = data?.reg?.role;
    if (!evId) return;
    let cancelled = false;
    checkShareGenerator({ variables: { eventId: evId, role: evRole || null } })
      .then((res) => {
        const cfg = res?.data?.shareGeneratorConfig;
        if (!cancelled && cfg && cfg.enabled) {
          setSgConfig(cfg);
          setSgCtaOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  // Use the custom screen size hook
  const { isSmallScreen, windowWidth, debugInfo } = useScreenSize();

  // Debug logging
  console.log("Screen Size Debug:", debugInfo);
  const url = encodeURIComponent(window.location.href);
  const title = encodeURIComponent(data?.reg?.eventName || "Event");
  const linkText = "Check it out here";
  // console.log(link === "test-visitor-form");

  const image =
    link === "test-visitor-form"
      ? `${IMAGES_API_URL}banner/cse-landing-banner.jpg`
      : link === "nama-online-delegate-2025" ||
          link === "nama-onSite-delegate-2025" ||
          link === "nama-iframe-delegate-2025"
        ? `https://eventegration.com/nama_banner.jpg`
        : `${IMAGES_API_URL}banner/${data?.reg?.eventId}.jpeg`;
  let parsedShareContent = data?.reg?.shareContent
    ? JSON.parse(data?.reg?.shareContent)
    : null;

  const currentUrl = window.location.href;
  const shareContent = `${
    parsedShareContent || "Join this event!"
  } ${currentUrl}`;
  let featuresList = [];
  if (featureData && featureData?.getFeatureList) {
    featuresList = JSON.parse(featureData?.getFeatureList.features);
  }

  // Parse invitation data if available
  const invitationData = data?.reg?.invitationData
    ? JSON.parse(data.reg.invitationData)
    : {};
  console.log("🔍 Invitation Data:", invitationData);
  // Check for social media features and determine available providers (memoized to prevent re-renders)
  const availableSocialProviders = React.useMemo(() => {
    const providers = [];
    if (featuresList.includes("register-facebook")) {
      providers.push("facebook");
    }
    if (featuresList.includes("register-linkedin")) {
      providers.push("linkedin");
    }
    if (featuresList.includes("register-google")) {
      providers.push("google");
    }
    return providers;
  }, [featuresList]);

  // If no social media features are enabled, default to manual registration
  const hasSocialFeatures = availableSocialProviders.length > 0;

  // Initialize registration mode based on features (only run once when features are loaded)
  const [registrationModeInitialized, setRegistrationModeInitialized] =
    React.useState(false);

  React.useEffect(() => {
    // Only initialize once when features are loaded for the first time
    if (!featureLoading && featureData && !registrationModeInitialized) {
      console.log("Initializing registration mode:", {
        hasSocialFeatures,
        availableSocialProviders,
        featuresList,
      });

      // Check if manual registration is enabled alongside social features
      const hasManualEnable = featuresList.includes("manual-enable");

      if (!hasSocialFeatures) {
        setRegistrationMode("manual");
      } else if (hasManualEnable) {
        // If manual-enable feature exists, always show choice (including manual option)
        setRegistrationMode("choice");
      } else {
        // Default behavior for social features without manual-enable
        setRegistrationMode("choice");
      }

      setRegistrationModeInitialized(true);
    }
  }, [
    featureLoading,
    featureData,
    hasSocialFeatures,
    registrationModeInitialized,
  ]);
  const twitterImage = `https://eventegration.com/posts/${data?.reg?.eventId}/twitter.jpeg`;
  const facebookImage = `https://eventegration.com/posts/${data?.reg?.eventId}/facebook.jpeg`;
  let description = null;
  let encodedDescription = null;
  let urlRegex = null;
  let foundUrls = null;
  let extractedUrl = null;
  let twitterShareLink = null;
  let linkedinShareLink = null;
  let whatsappShareLink = null;
  let fbShareLink = null;

  if (featuresList.includes("ShareButton")) {
    description = parsedShareContent;
    encodedDescription = encodeURIComponent(description);
    urlRegex = /(https?:\/\/[^\s]+)/g;
    const foundUrls =
      Array.isArray(description) && description.length > 0
        ? description.match(urlRegex)
        : "";

    extractedUrl = foundUrls ? foundUrls[0] : null;

    twitterShareLink = `https://twitter.com/intent/tweet?text=${encodedDescription}`;
    linkedinShareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${extractedUrl}`;
    whatsappShareLink = `https://api.whatsapp.com/send?text=${title} - ${encodedDescription}`;
    fbShareLink = `https://www.facebook.com/sharer/sharer.php?u=${extractedUrl}&quote=${encodedDescription}&picture=${image}`;
  }

  React.useEffect(() => {
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: "273754185393849",
        autoLogAppEvents: true,
        xfbml: true,
        version: "v11.0",
      });
    };
  }, []);

  const handleFBShare = () => {
    window.FB.ui(
      {
        method: "share_open_graph",
        action_type: "og.shares",
        action_properties: JSON.stringify({
          object: {
            "og:url": extractedUrl,
            "og:title": title,
            "og:description": description,
            "og:image": image,
          },
        }),
      },
      function (response) {
        console.log("response", response);
      },
    );
  };
  const handleReset = () => {
    setResponse("");
  };

  // Social Media Registration Handlers
  const handleSocialChoice = async (provider) => {
    try {
      setSelectedProvider(provider);

      // Skip email verification - go directly to social authentication
      setRegistrationMode("social");

      // Start social authentication immediately
      await socialAuth.startSocialAuth(provider);

      console.log("🔍 Social authentication started for:", provider);
    } catch (error) {
      console.error("🚨 Error starting social authentication:", error);
      setSnackbar({
        open: true,
        message: `Failed to start ${provider} authentication: ${error.message}`,
        severity: "error",
      });
    }
  };

  // Handle email verification with modern notification
  // const handleEmailVerification = async (email) => {
  //   try {
  //     // Check if email already exists
  //     const { data: emailCheck } = await checkEmailMutation({
  //       variables: { email, link },
  //     });

  //     if (emailCheck?.checkEmail === "Email already exists") {
  //       setSnackbar({
  //         open: true,
  //         message:
  //           "This email is already registered. Please use a different email or proceed with manual registration.",
  //         severity: "warning",
  //       });
  //       return;
  //     }

  //     // Email is available, proceed with social auth
  //     // setVerifiedEmail(email);
  //     // setEmailVerificationStep(false);
  //     setRegistrationMode("social");

  //     // Start social authentication
  //     await socialAuth.startSocialAuth(selectedProvider);
  //   } catch (error) {
  //     console.error("Email verification failed:", error);
  //     setSnackbar({
  //       open: true,
  //       message: "Email verification failed. Please try again.",
  //       severity: "error",
  //     });
  //   }
  // };

  const handleManualChoice = () => {
    setRegistrationMode("manual");
  };

  const handleSocialRegistrationComplete = async (formData) => {
    try {
      // Use the social media email directly (no need for verified email)
      const socialEmail =
        socialAuth.socialData?.email || socialAuth.mappedData?.Email;

      const dataWithEmail = {
        ...formData,
        Email: socialEmail,
        email: socialEmail,
      };

      // Add referral data if present
      const finalFormData = prepareReferralFormData(
        dataWithEmail,
        referralParams,
      );

      console.log("🔍 Social registration data:", {
        formData,
        socialEmail,
        dataWithEmail,
        referralParams,
      });

      // Use the social auth hook's built-in completion method
      const result = await socialAuth.completeRegistration(finalFormData);

      if (result) {
        setResponse(result);
        setRegistrationMode("completed");
      }
    } catch (error) {
      console.error("Social registration failed:", error);
      setSnackbar({
        open: true,
        message: `Registration failed: ${error.message}`,
        severity: "error",
      });
    }
  };

  // const handleBackToChoice = () => {
  //   setRegistrationMode("choice");
  //   setSelectedProvider(null);
  // };

  // Handle snackbar close
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  React.useEffect(() => {
    responseBoxRef?.current?.scrollIntoView();
  }, [response]);

  // Handle PMT errors with modern notification
  React.useEffect(() => {
    if (pmtError) {
      setSnackbar({
        open: true,
        message: pmtError?.message || "Payment link error occurred",
        severity: "error",
      });
    }
  }, [pmtError]);

  // Handle social auth errors with modern notification
  React.useEffect(() => {
    if (socialAuth.error) {
      setSnackbar({
        open: true,
        message: `Social Authentication Error: ${socialAuth.error}`,
        severity: "error",
      });
      setRegistrationMode("choice"); // Go back to choice screen
    }
  }, [socialAuth.error]);

  // Show enhanced loading until all required data is fetched and registration mode is determined

  // Invitation-only gate. api-reg resolves this per LINK now (`reg.onlyPmt` = the link's own
  // override, else the role's `onlyPMT` feature), so two links of the same role can differ — e.g.
  // an Onsite link stays open while the Online one needs an invitation. Falls back to the feature
  // list when the API predates the field, so an older api-reg behaves exactly as before.
  const onlyPmtGate =
    typeof data?.reg?.onlyPmt === "boolean"
      ? data.reg.onlyPmt
      : featuresList.includes("onlyPMT");
  if (onlyPmtGate && !pmt && !uid) {
    return (
      <StatusScreen
        variant="info"
        title="Invitation Required"
        message="This registration is available by invitation only. Please use the private link the event organizer shared with you to continue."
        details="ℹ️ Contact the event organizer to get your registration link."
        eventId={data?.reg?.eventId}
        eventName={data?.reg?.eventName}
      />
    );
  }
  if (pmtError) {
    return (
      <StatusScreen
        variant="error"
        title={pmtError.message || "Link Not Working"}
        message="There was a problem with your invitation link — it may have expired or be invalid. Please try again or contact the event organizer."
        actions={[{ label: "Retry", primary: true, onClick: () => window.location.reload() }]}
        eventId={data?.reg?.eventId}
      />
    );
  }

  // Enhanced GraphQL error handling
  const errorAnalysis = analyzeGraphQLError(error);

  if (
    isGraphQLError(
      error,
      "This registration page has been deactivated by admin",
    )
  ) {
    return (
      <StatusScreen
        variant="warning"
        title="Registration Closed"
        message="This registration form has been deactivated by the event organizer and is no longer accepting entries."
        details="If you believe this is a mistake, please contact the event organizer."
      />
    );
  }

  if (
    isGraphQLError(error, "Link not found") ||
    errorAnalysis.type === "not_found"
  ) {
    return <LinkNotFound />;
  }

  // Handle GraphQL errors with user-friendly messages - MUST come first. All states now render the
  // shared StatusScreen with the correct TONE (previously a duplicate showed in a GREEN success card).
  if (error) {
    const errorMessage = error.message || "An error occurred";
    const stEventId = data?.reg?.eventId;

    // Invitation declined / contact organizer
    if (
      errorMessage.includes("declined this invitation") ||
      errorMessage.includes("contact the organizer")
    ) {
      return (
        <StatusScreen
          variant="info"
          title="Invitation Status"
          message={errorMessage}
          eventId={stEventId}
        />
      );
    }
    // Already registered (duplicate) — WARNING tone (was wrongly green)
    if (
      errorMessage.includes("already registered") ||
      errorMessage.includes("You have already registered")
    ) {
      return (
        <StatusScreen
          variant="warning"
          title="Already Registered"
          message={errorMessage}
          eventId={stEventId}
        />
      );
    }
    // Capacity full
    if (
      errorMessage.includes("Registration full") ||
      errorMessage.includes("capacity is full") ||
      errorMessage.includes("seats already taken")
    ) {
      return (
        <StatusScreen
          variant="warning"
          title="Registration Full"
          message={errorMessage}
          details="All available seats have been taken. Please contact the event organizer if you think this is an error."
          eventId={stEventId}
        />
      );
    }
    // Expired invitation
    if (
      errorMessage.includes("expired") ||
      errorMessage.includes("Invitation has expired")
    ) {
      return (
        <StatusScreen
          variant="error"
          title="Invitation Expired"
          message={errorMessage}
          eventId={stEventId}
        />
      );
    }
    // Invalid invitation
    if (
      errorMessage.includes("Invalid invitation") ||
      errorMessage.includes("Invitation not found")
    ) {
      return (
        <StatusScreen
          variant="error"
          title="Invalid Invitation"
          message={errorMessage}
          eventId={stEventId}
        />
      );
    }
    // Generic
    return (
      <StatusScreen
        variant="error"
        title="Something Went Wrong"
        message={errorMessage}
        actions={[{ label: "Retry", primary: true, onClick: () => window.location.reload() }]}
        eventId={stEventId}
      />
    );
  }


  // Handle general GraphQL errors (but not rejected invitations)
  if (
    error &&
    errorAnalysis.type === "general" &&
    !errorAnalysis.isRejected &&
    !error?.message?.includes("declined this invitation") &&
    !error?.message?.includes("contact the organizer")
  ) {
    return (
      <StatusScreen
        variant="error"
        title="Registration Error"
        message={errorAnalysis.message}
        details="⚠️ If this error persists, please contact the event organizer for assistance."
        actions={[{ label: "Retry", primary: true, onClick: () => window.location.reload() }]}
        eventId={data?.reg?.eventId}
      />
    );
  }

  if (pmt && pmt.isExhausted) {
    return (
      <StatusScreen
        variant="warning"
        title="Registration Limit Reached"
        message={`${
          pmt.company ? `${pmt.company} has` : "The sponsor has"
        } used up all the registrations allowed on this invitation link.`}
        details="💡 Try contacting the event organizer for alternative registration options."
        eventId={data?.reg?.eventId}
      />
    );
  }

  // if (error) {
  //   return (
  //     <Container>
  //       <Typography color="error" variant="h4">
  //         {error?.message}
  //         {/* <pre>{JSON.stringify(error, null, 2)}</pre> */}
  //       </Typography>
  //     </Container>
  //   );
  // }
  // console.log("?>>>>>>>>", data);
  if (loading || featureLoading || pmtLoading || !registrationModeInitialized) {
    return (
      <LoadingState
        type="registration"
        message="Loading registration form..."
        eventId={data?.reg?.eventId}
        eventName={data?.reg?.eventName}
        sx={{ minHeight: "80vh" }}
      />
    );
  }
  if (!data?.reg?.eventId) {
    return <LinkNotFound />;
  }

  // Per-link header config (owner 2026-09-20). null/absent = the historical behaviour, so a link
  // that was never configured — and an api-reg that predates the field — render exactly as before.
  const headerMode =
    data.reg.headerMode === "none" ||
    data.reg.headerMode === "custom" ||
    data.reg.headerMode === "event"
      ? data.reg.headerMode
      : null;
  const headerImageUrl = data.reg.headerImageUrl || null;
  // 'event' is an explicit choice and therefore beats the "…iframe…" slug rule; 'none' hides it;
  // unset falls back to that rule.
  const showEventBanner =
    headerMode === "event" || (headerMode === null && !isEmbed);

  return (
    <ThemeProvider theme={customTheme}>
      <Helmet>
        <title>{data?.reg?.eventName || "Event Registration"}</title>

        {/* Primary Meta Tags */}
        <meta name="title" content={data.reg.eventName} />
        <meta name="description" content={parsedShareContent} />

        {/* Open Graph / Facebook */}
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={data.reg.eventName} />
        <meta property="og:description" content={parsedShareContent} />
        <meta property="og:image" content={facebookImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        {/* {data?.reg?.fbAppId && (
          <meta property="fb:app_id" content={data.reg.fbAppId} />
        )} */}

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={window.location.hostname} />
        <meta property="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={data.reg.eventName} />
        <meta name="twitter:description" content={parsedShareContent} />
        <meta name="twitter:image" content={twitterImage} />

        {/* Favicon */}
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href={`${IMAGES_API_URL}favicon/${data.reg.eventId}/apple-touch-icon.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href={`${IMAGES_API_URL}favicon/${data.reg.eventId}/favicon-32x32.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${IMAGES_API_URL}favicon/${data.reg.eventId}/favicon-16x16.png`}
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href={`${IMAGES_API_URL}favicon/${data.reg.eventId}/favicon.ico`}
        />
        <link
          rel="manifest"
          href={`${IMAGES_API_URL}favicon/${data.reg.eventId}/site.webmanifest`}
        />
      </Helmet>
      <Box sx={{ backgroundColor: parsedTheme.background, minHeight: "100vh" }}>
        <Container
          maxWidth="lg"
          sx={{
            py: { xs: 3, md: 4 },
            pt: { xs: 3, md: 4 },
            px: { xs: 1, md: 2 },
          }}
        >
          {/* What sits above the form heading.
              - The link's own `headerMode` wins when it is set (Admin → Role Links, or Reception →
                Registration links for a superadmin): 'none' = nothing, 'custom' = that image.
                Owner 2026-09-20 — this replaced a growing list of hardcoded event ids/slugs, and it
                deliberately OVERRIDES the "…iframe…" rule below, because a client can want their own
                logo on the embedded link too.
              - Otherwise: hidden for embedded ("…iframe…") links (the host page supplies its own
                branding, same as /iframe/:link), else the event banner. See `isEmbed` above. */}
          {headerMode === "custom" && headerImageUrl && (
            <Fade in={true} timeout={1000}>
              <Box
                sx={{
                  mb: 4,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={headerImageUrl}
                  alt="Event Logo"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "160px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Fade>
          )}
          {showEventBanner && data.reg.eventId && (
            <Fade in={true} timeout={1000}>
              <Box
                sx={{
                  mb: 4,
                  borderRadius: 1,
                  overflow: "hidden",
                  // boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                  position: "relative",
                  // "&::before": {
                  //   content: '""',
                  //   position: "absolute",
                  //   top: 0,
                  //   left: 0,
                  //   right: 0,
                  //   bottom: 0,
                  //   background: `linear-gradient(45deg, ${parsedTheme.primary}10, ${parsedTheme.secondary}10)`,
                  //   zIndex: 1,
                  // },
                }}
              >
                {String(data.reg.eventId) === "5087644490" ? (
                  <img
                    src={`https://api-admin.eventegration.com/public/logo/${data.reg.eventId}.png`}
                    width="100%"
                    alt="Event Logo"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    style={{
                      maxHeight: "150px",
                      objectFit: "contain",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                ) : (
                  <img
                    src={
                      link === "istc-cs-online-2024"
                        ? `${IMAGES_API_URL}banner/cse-landing-banner.jpg`
                        : link === "nama-online-delegate-2025" ||
                            link === "nama-onSite-delegate-2025" ||
                            link === "nama-iframe-delegate-2025"
                          ? `https://eventegration.com/nama_banner.jpg`
                          : `${IMAGES_API_URL}banner/${data.reg.eventId}.jpeg`
                    }
                    width="100%"
                    alt="Event Banner"
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    style={{
                      maxHeight: "300px",
                      objectFit: "cover",
                      position: "relative",
                      zIndex: 2,
                    }}
                  />
                )}
              </Box>
            </Fade>
          )}

          {!response && (
            <Grow in={true} timeout={1200}>
              <Box
                sx={{
                  textAlign: "center",
                  mb: 4,
                  maxWidth: "1100px",
                  mx: "auto",
                }}
              >
                {data.reg.formHeading ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: data.reg.formHeading }}
                  />
                ) : (
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: "bold",
                      color: parsedTheme.primary,
                      mb: 1,
                    }}
                  >
                    {data.reg.role} Registration
                  </Typography>
                )}
              </Box>
            </Grow>
          )}

          {/* <Zoom in={true} timeout={1500}> */}
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 2,
              maxWidth: "1100px",
              mx: "auto",
              backgroundColor: parsedTheme.background,
              border: `1px solid ${parsedTheme.primary}20`,
              boxShadow: "0 12px 40px rgba(0, 0, 0, 0.08)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: `linear-gradient(90deg, ${parsedTheme.primary}, ${parsedTheme.secondary})`,
              },
            }}
          >
            {response ? (
              // Check if it's a rejection response (contains custom rejection HTML)
              response.includes("Invitation Status") ||
              response.includes("declined this invitation") ? (
                // Show rejection response directly without FormSummary
                <div dangerouslySetInnerHTML={{ __html: response }} />
              ) : (
                // Show regular registration success with FormSummary
                <FormSummary
                  response={response}
                  onReset={handleReset}
                  showResetButton={false}
                  showShareButton={featuresList.includes("ShareButton")}
                  onShare={() => setShareDialogOpen(true)}
                  showShareGenerator={!!sgConfig?.enabled}
                  onShareGenerator={openShareGenerator}
                  shareGeneratorLabel="Share you're attending"
                  enableAnotherRegistration={featuresList.includes(
                    "Enable Another Registration Button",
                  )}
                  resetButtonText="Another registration?"
                  featuresList={featuresList}
                />
              )
            ) : data.reg.isInvitation ? (
              // Show invitation registration form
              <InvitationRegistrationForm
                invitationData={JSON.parse(data.reg.invitationData || "{}")}
                fields={data.reg.fields}
                eventName={data.reg.eventName}
                capacity={JSON.parse(data.reg.capacity || "{}")}
                uid={uid}
                isMobile={isSmallScreen}
                onSuccess={(result) => {
                  console.log("Invitation registration success:", result);

                  // Check if it's a rejection with custom response
                  if (result.isRejection && result.customRejectionResponse) {
                    setResponse(result.customRejectionResponse);
                  } else {
                    // Regular registration success
                    setResponse(`<div style="text-align: center; padding: 40px; font-family: Arial, sans-serif;">
                        <h2 style="color: #4CAF50; margin-bottom: 20px;">${result.message}</h2>
                       </div>`);
                  }
                }}
                onError={(error) => {
                  console.error("Invitation registration error:", error);
                  setSnackbar({
                    open: true,
                    message: error.message || "Registration failed",
                    severity: "error",
                  });
                }}
              />
            ) : hasSocialFeatures && registrationMode === "choice" ? (
              // Show social media registration choice page
              <SocialRegistrationChoice
                onSocialChoice={handleSocialChoice}
                onManualChoice={handleManualChoice}
                eventName={data.reg.eventName}
                availableProviders={availableSocialProviders}
                showManualOption={featuresList.includes("manual-enable")}
              />
            ) : registrationMode === "social" &&
              selectedProvider &&
              socialAuth.loading ? (
              // Show loading state when social authentication is in progress
              <LoadingState
                type="registration"
                message={`Connecting with ${selectedProvider}...`}
                eventId={data.reg.eventId}
                eventName={data.reg.eventName}
                sx={{ minHeight: "100vh" }}
              />
            ) : registrationMode === "social" &&
              selectedProvider &&
              socialAuth.socialData ? (
              // Show social media completion form with actual data from social auth
              <SocialRegistrationForm
                socialData={socialAuth.socialData}
                mappedData={uid ? invitationData : socialAuth.mappedData}
                missingFields={socialAuth.missingFields}
                allFields={data.reg.fields}
                onSubmit={handleSocialRegistrationComplete}
                provider={selectedProvider}
                loading={socialAuth.loading}
                verifiedEmail={socialAuth.socialData?.email} // Use social email directly
                eventId={data.reg.eventId}
                uid={uid}
                isMobile={isSmallScreen}
              />
            ) : (
              // Show regular registration form
              <>
                {console.log("🔍 About to render RegForm with uid:", uid)}
                <RegForm
                  fields={data.reg.fields}
                  onSubmit={(res) => setResponse(res)}
                  onRegisteredEmail={setSgRegEmail}
                  eventId={data.reg.eventId}
                  role={data.reg.role}
                  featuresList={featuresList}
                  pmt={pmt || null}
                  chkmid={chkmid || false}
                  iplink={iplink || false}
                  customTheme={parsedTheme}
                  referralParams={referralParams}
                  uid={uid}
                  invitationData={invitationData}
                  isMobile={isSmallScreen}
                />
              </>
            )}
          </Paper>
          {/* </Zoom> */}

          {/* Share Dialog */}
          {featuresList.includes("ShareButton") && (
            <ShareDialog
              open={shareDialogOpen}
              handleClose={() => setShareDialogOpen(false)}
              title={data.reg.eventName}
              description={parsedShareContent}
              eventId={data.reg.eventId}
            />
          )}

          {/* SPE: post-registration Share Generator CTA — shown only when this role's Share Generator is
              enabled. Opens the "I'm attending" card generator so the new attendee can share right away. */}
          <Dialog
            open={sgCtaOpen}
            onClose={() => setSgCtaOpen(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, textAlign: "center", p: 1 } }}
          >
            <DialogContent sx={{ pt: 4 }}>
              <Typography sx={{ fontSize: 44, mb: 1 }}>🎉</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {sgConfig?.shareTitle || `You're registered — spread the word!`}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your personalized “I’m attending” card and share it with your network in seconds.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                size="large"
                sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", mb: 1 }}
                onClick={openShareGenerator}
              >
                ◈ Share you’re attending →
              </Button>
              <Button
                variant="text"
                fullWidth
                sx={{ textTransform: "none", color: "text.secondary" }}
                onClick={() => setSgCtaOpen(false)}
              >
                Maybe later
              </Button>
            </DialogContent>
          </Dialog>

          {/* {data.reg?.eventId && String(data.reg.eventId) === "5087644490" && (
            <Fade in={true} timeout={1500}>
              <Box
                sx={{
                  mb: 4,
                  mt: 4,
                  borderRadius: 1,
                  overflow: "hidden",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                  position: "relative",
                }}
              >
                <img
                  src={`${IMAGES_API_URL}banner/${data.reg.eventId}.jpeg`}
                  width="100%"
                  alt="Event Banner"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                  style={{
                    maxHeight: "300px",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Fade>
          )} */}

          <Fade in={true} timeout={2000}>
            <Box
              sx={{
                py: 4,
                mt: 2,
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                {(() => {
                  const branding = getBranding(data?.reg?.eventId);
                  return (
                    <>
                      <Typography
                        color="text.secondary"
                        fontSize={12}
                        sx={{ mb: 1 }}
                      >
                        Powered by {branding.companyName}
                      </Typography>
                      <a
                        href={branding.websiteUrl}
                        style={{ textDecoration: "none" }}
                      >
                        <img
                          src={branding.logoUrl || LogoPng}
                          width={160}
                          alt={`${branding.companyName} Logo`}
                          style={{ opacity: 0.85 }}
                        />
                      </a>
                    </>
                  );
                })()}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Company/organization field-name variants (form-defined, so they vary per event). Used to prefill +
// lock the company field from a sponsor pmt link (owner 2026-07-17). Keep in sync with api-reg's
// detectCompanyColumn list. NOTE: this is UX only — api-reg server-enforces the company on pmt links.
export const COMPANY_FIELD_VARIANTS = [
  "company",
  "companyname",
  "company name",
  "company_name",
  "organization",
  "organization name",
  "organization_name",
  "organisation",
  "organisation name",
  "organisation_name",
];
export const isCompanyField = (name) =>
  COMPANY_FIELD_VARIANTS.includes(String(name || "").trim().toLowerCase());

export function RegForm({
  fields,
  onSubmit,
  onRegisteredEmail,
  eventId,
  role,
  featuresList,
  registrationId,
  pmt,
  chkmid,
  iplink,
  customTheme,
  referralParams,
  prefilledEmail,
  socialProvider,
  uid,
  invitationData,
  isMobile,
}) {
  // Parse theme for component use with defensive fallback
  const parsedTheme = parseTheme(customTheme) || {
    primary: "#000000",
    secondary: "#0066cc",
    background: "#ffffff",
    surface: "#f5f5f5",
  };

  const [openModal, setOpenModal] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [clientId, setClientId] = React.useState(null);
  const [price, setPrice] = React.useState(null);
  const [allData, setAllData] = React.useState(null);
  const [apiurl, setApiurl] = React.useState(null);
  const [secretKey, setSecretKey] = React.useState(null);
  const [publicKey, setPublicKey] = React.useState(null);
  const [emailError, setEmailError] = React.useState("");
  // Duplicate-email message shows as a TOAST (bottom of screen), NOT as a red line above the submit button.
  // Owner ask: "agar real-time nahi kar paa rahe to toast me dikhao, button ke upar dikhana bura lagta hai."
  const [dupToast, setDupToast] = React.useState("");
  const [selectedPaymentOption, setSelectedPaymentOption] = React.useState("");
  const [paymentFailedLoading, setPaymentFailedLoading] = React.useState(false);
  const [token, setToken] = React.useState(null);
  const [captchaOff, setCapthaoff] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState(null);
  const [selectedMobileCode, setSelectedMobileCode] = React.useState(null);
  const [discount, setDiscount] = useState(false);
  const [memberDiscount, setMemberDiscount] = useState(0);
  const [promoDiscount, setPromoDiscount] = useState(0);
  // Flat (fixed-amount) promo discount. A coupon is EITHER a percentage (promoDiscount)
  // OR a flat amount (promoFlat) — the backend returns exactly one; the other stays 0.
  // Without this, flat coupons (discountValue) were dropped and the customer paid full price.
  const [promoFlat, setPromoFlat] = useState(0);
  const [memberIdValue, setMemberIdValue] = useState("");
  const [isMemberIdValid, setIsMemberIdValid] = useState(false);
  const [memberIdError, setMemberIdError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null);
  const [promoMessage, setPromoMessage] = useState("");

  // State for "Other" field inputs - keyed by field name
  const [otherFieldStates, setOtherFieldStates] = useState({});

  // Handler to update "Other" field state
  const updateOtherFieldState = (fieldName, updates) => {
    setOtherFieldStates((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        ...updates,
      },
    }));
  };

  // Get "Other" field state for a specific field
  const getOtherFieldState = (fieldName) => {
    return (
      otherFieldStates[fieldName] || {
        showOtherInput: false,
        otherValue: "",
      }
    );
  };
  const [originalPrice, setOriginalPrice] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("OMR");

  // Memoize fields processing to prevent re-renders
  const memoizedFields = React.useMemo(
    () =>
      fields?.map((f) => ({
        ...f,
        sx: f.sx ? JSON.parse(f.sx) : {},
      })),
    [fields],
  );

  // const debouncedMemberId = useDebounce(memberIdValue, 500);

  // Query for member ID validation
  const [checkMemberId] = useLazyQuery(CHECK_MEMBER, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.checkMemberId.isValid) {
        setIsMemberIdValid(true);
        setMemberIdError("");
        // Set discount from API response
        setMemberDiscount(parseFloat(data.checkMemberId.discount) || 0);
      } else {
        setIsMemberIdValid(false);
        setMemberIdError(data.checkMemberId.message);
      }
    },
    onError: (e) => setMemberIdError(e?.message || "Validation failed"),
  });

  // Trigger validation on debounced value
  // useEffect(() => {
  //   if (debouncedMemberId && featuresList.includes("MemberCheck")) {
  //     checkMemberId({ variables: { memberId: debouncedMemberId, link } });
  //   }
  // }, [debouncedMemberId]);

  // Promo code validation query
  const [validatePromo] = useLazyQuery(VALIDATE_PROMO, {
    onCompleted: (data) => {
      // NOTE: the Apply button passes its own onCompleted (below) which sets the discount
      // state. This default handler only sets the status/message and must NOT assume a
      // percentage coupon (flat coupons return discountValue, not discountPercent).
      if (data.validatePromo.status) {
        const pct = data.validatePromo.discountPercent;
        const flat = data.validatePromo.discountValue;
        setPromoStatus(data.validatePromo.message);
        setPromoMessage(
          pct
            ? `${pct}% discount applied!`
            : flat
              ? `${flat} discount applied!`
              : data.validatePromo.message || "Promo code applied.",
        );
      } else {
        setPromoStatus("invalid");
        setPromoMessage(data.validatePromo.message);
      }
    },
  });

  const [isDefaultCountrySelected, setIsDefaultCountrySelected] =
    React.useState(false);
  const [errorInPaymentInitialization, setErrorInPaymentInitialization] =
    React.useState(false);
  const [isPopupNotOpened, setIsPopupNotOpened] = React.useState(false);

  // Memoize parsed registration to prevent re-parsing on every render
  const parsedRegistration = React.useMemo(
    () =>
      registrationId && typeof registrationId === "string"
        ? JSON.parse(registrationId)
        : registrationId,
    [registrationId],
  );

  const [showFamilyMembers, setShowFamilyMembers] = React.useState(false);
  const navigate = useNavigate();
  const { link } = useParams();
  const [email, setEmail] = React.useState(prefilledEmail || "");
  const { checkEmail: isEmailTaken, loading: emailCheckLoading } =
    useCheckEmail(
      !featuresList?.includes("duplicateEmailAllowed") ? email : null,
      !featuresList?.includes("duplicateEmailAllowed") ? link : null,
    );

  // Initialize email when prefilled email is provided
  React.useEffect(() => {
    if (prefilledEmail) {
      setEmail(prefilledEmail);
    }
  }, [prefilledEmail]);

  // When the real-time check flags the email as taken, surface it as the TOAST too (not just the inline
  // field hint) — so the duplicate message is consistent whether it's caught live or at submit. Clears when
  // the email becomes valid again.
  // The live check returns a role-specific message ("This email is already registered under 'visitor' for
  // this event.") — treat ANY message containing "already registered" as a hit (back-compat with the old
  // "Email already exists" sentinel).
  const emailTakenHit =
    typeof isEmailTaken === "string" &&
    (isEmailTaken.toLowerCase().includes("already registered") ||
      isEmailTaken === "Email already exists");
  React.useEffect(() => {
    if (emailTakenHit && !featuresList?.includes("duplicateEmailAllowed")) {
      // Show the role-specific message from the backend in the toast.
      setDupToast(
        isEmailTaken && isEmailTaken.toLowerCase().includes("already registered")
          ? isEmailTaken
          : "This email is already registered.",
      );
    } else {
      // Owner 2026-09-12: the toast no longer auto-closes, so it MUST be cleared here once the
      // email stops being a duplicate — otherwise a stale "already registered" warning sticks on
      // screen while the user types a fresh address. The original comment above claimed this
      // happened; the `else` branch was missing, and the 6s autoHide was hiding the bug.
      setDupToast("");
    }
  }, [isEmailTaken, emailTakenHit, featuresList]); // eslint-disable-line react-hooks/exhaustive-deps

  const [otpModalProps, setOtpModalProps] = React.useState({
    isOpen: false,
    mobile: null,
    eventId: null,
    onVerify: null,
  });
  const turnstile = useTurnstile();
  const { paymentData } = usePaymentDetail(eventId, role);

  React.useEffect(() => {
    if (link && link.includes("survey")) {
      navigate(`/survey/${link}`);
    }
  }, [link, navigate]);

  // Always call the geolocation hook but only use results when feature is enabled
  const geoLocationHook = useGeoLocation();
  const shouldUseGeoLocation = featuresList?.includes("GeoLocation");

  const {
    country: geoCountry,
    error: geoError,
    isLoading: geoIsLoading,
  } = shouldUseGeoLocation
    ? geoLocationHook
    : { country: null, error: null, isLoading: false };

  // console.log(">>>>>", geoCountry && typeof geoCountry, geoCountry);

  React.useEffect(() => {
    if (shouldUseGeoLocation && !isDefaultCountrySelected && geoCountry) {
      const isdCode = countries.find((c) => c.code === geoCountry);
      setSelectedCountry(isdCode?.label);
      setIsDefaultCountrySelected(true);
      console.log(isdCode, ">>>><<<<");
      setSelectedMobileCode(isdCode?.dial_code);
    }
  }, [geoCountry, isDefaultCountrySelected, shouldUseGeoLocation]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const [
    doPaymentRegister,
    { loading: PaymentRegisterLoading, error: PaymentRegisterError },
  ] = useMutation(PAYMENT_REGISTER);

  const handlePaymentSuccess = async (details, actions) => {
    console.log("running actions", details);
    try {
      // Check for different payment status formats (PayPal vs others)
      const isPaymentSuccessful =
        details?.status === "COMPLETED" || // PayPal format
        details?.payment_status === "COMPLETED" || // Other payment methods
        details?.payment_status === "success" ||
        details?.status === "success";

      if (isPaymentSuccessful) {
        handleClose();

        // Extract amount and currency based on payment method
        let paymentAmount, paymentCurrency;
        if (details?.purchase_units?.[0]?.amount) {
          // PayPal format
          paymentAmount = details.purchase_units[0].amount.value;
          paymentCurrency = details.purchase_units[0].amount.currency_code;
        } else {
          // Other payment methods
          paymentAmount = details.amount;
          paymentCurrency = details.currency;
        }

        const updatedAllData = {
          ...allData,
          payment_status: "success",
          payment: `${paymentAmount} ${paymentCurrency}`,
          status: "accept",
        };
        const reglink = pmt ? pmt.param : null;
        const mobile = allData?.mobile || allData?.phone || allData?.Mobile;
        const email = allData?.email || allData?.Email;
        const calling_code =
          allData?.calling_code ||
          allData?.country_code ||
          allData?.CallingCode;
        const updatedMobile =
          mobile && calling_code
            ? `${calling_code.replace("+", "")}${mobile}`
            : null;
        const isMobileOTPRequired =
          featuresList?.includes("MobileOTP") ||
          featuresList?.includes("mobileOTP") ||
          featuresList?.includes("mobile_otp");
        const isEmailOTPRequired =
          featuresList?.includes("EmailOTP") ||
          featuresList?.includes("emailOTP") ||
          featuresList?.includes("email_otp");
        const isLegacyOTPRequired = featuresList?.includes("OTP"); // Support legacy OTP feature

        // Debug: Check if EmailOTP is in features with different casing
        const hasEmailOTP = featuresList.some(
          (feature) =>
            feature.toLowerCase().includes("emailotp") ||
            feature.toLowerCase().includes("email_otp") ||
            feature.toLowerCase().includes("email-otp"),
        );

        // Additional debug for form data

        if (isMobileOTPRequired || isEmailOTPRequired || isLegacyOTPRequired) {
          // Handle legacy OTP - default to mobile if available, otherwise email
          let finalMobileOTP = isMobileOTPRequired;
          let finalEmailOTP = isEmailOTPRequired;

          if (isLegacyOTPRequired) {
            if (updatedMobile) {
              finalMobileOTP = true;
            } else if (email) {
              finalEmailOTP = true;
            }
          }

          // Validate required data for OTP
          if (finalMobileOTP && !updatedMobile) {
            console.error("Mobile OTP required but no mobile number provided");
            return;
          }
          if (finalEmailOTP && !email) {
            console.error("Email OTP required but no email provided");
            return;
          }

          console.log("Showing OTP Modal with:", {
            mobile: finalMobileOTP ? updatedMobile : null,
            email: finalEmailOTP ? email : null,
            eventId,
            role,
          });

          // Show OTP Modal and wait for verification
          const otpVerified = await new Promise((resolve) => {
            setOtpModalProps({
              isOpen: true,
              mobile: finalMobileOTP ? updatedMobile : null,
              email: finalEmailOTP ? email : null,
              eventId: eventId,
              role: role,
              onVerify: (success) => {
                setOtpModalProps({ ...otpModalProps, isOpen: false });
                resolve(success);
              },
            });
          });

          if (!otpVerified) {
            console.log("OTP verification failed.");
            return; // Stop further execution if OTP verification fails
          }
        }

        const result = await doPaymentRegister({
          variables: {
            link: JSON.stringify({ link, pmtLink: reglink, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
            jsonData: JSON.stringify({
              ...updatedAllData,
              promoCode,
              memberId: memberIdValue,
            }),
            transaction: JSON.stringify(details),
          },
        }).then((res) => res.data?.paymentRegister);

        if (featuresList.includes("Captcha") && result) {
          turnstile.reset();
          setToken(null);
          setCapthaoff(true);
        }
        setSelectedPaymentOption("");

        // console.log("hiiiiii");
        return onSubmit(result);
      }
      // Check for payment failure in different formats
      const isPaymentFailed =
        details?.status === "FAILED" || // PayPal format
        details?.payment_status === "FAILED" || // Other payment methods
        details?.payment_status === "failed" ||
        details?.status === "failed";

      if (isPaymentFailed) {
        handleClose();
        setPaymentFailedLoading(true);
      }
    } catch (error) {
      const paymentStatus =
        details?.status || details?.payment_status || "Unknown";
      setEmailError(
        `Payment Status: ${paymentStatus} <br /> Registration Status: ${error?.message} <br/> Please contact organizer to resolve this issue. Sorry for the inconvenience`,
      );
      console.error("Error during payment:", error);
    }
  };

  const handleModalOpen = () => {
    setOpenModal(true);
  };
  const handleModalClose = () => {
    setOpenModal(false);
  };

  // Memoize field value logic for UID links to prevent re-creation on every render
  const getFieldValue = React.useCallback(
    (field) => {
      if (uid && invitationData) {
        // Try direct match first
        if (invitationData[field.name] !== undefined) {
          return invitationData[field.name];
        }
        // Try label match
        if (field.label && invitationData[field.label] !== undefined) {
          return invitationData[field.label];
        }
      }
      return undefined;
    },
    [uid, invitationData],
  );

  // Memoize form configuration to prevent re-creation on every render
  const formConfig = React.useMemo(
    () => ({
      defaultValues: fields.reduce((a, r) => {
        let fieldValue;

        // For UID links, try to get value from invitation data
        if (uid) {
          fieldValue = getFieldValue(r);
        }

        if (fieldValue === undefined) {
          if (r.type === "checkbox") {
            fieldValue = !!parseInt(r.defaultValue || "0");
          } else if (r.type === "mcq") {
            fieldValue = [];
          } else if (r.name.toLowerCase() === "email" && prefilledEmail) {
            fieldValue = prefilledEmail;
          } else if (isCompanyField(r.name) && pmt?.company) {
            // Sponsor pmt link → prefill the company field with the sponsor's company (readonly below).
            fieldValue = pmt.company;
          } else {
            fieldValue = r.defaultValue || "";
          }
        }

        return {
          ...a,
          [r.name]: fieldValue,
        };
      }, {}),
      resolver: yupResolver(
        yup.object().shape({
          ...fields.reduce(
            (a, r) => ({ ...a, [r.name]: buildValidation(r, uid) }),
            {},
          ),
          ...fields
            .filter((r) => r.options?.some((op) => op === "$Other"))
            ?.reduce(
              (a, r) => ({
                ...a,
                [r.name + "_other"]: yup
                  .string()
                  .min(r.minLength, "Too long")
                  .max(r.maxLength, "Too long")
                  // .matches(r.pattern, "Invalid pattern")
                  .test({
                    message: "Required",
                    test: (a, p) =>
                      p.parent[r.name]?.includes("$Other") ? !!a : true,
                  }),
              }),
              {},
            ),
        }),
      ),
    }),
    [fields, uid, getFieldValue, prefilledEmail],
  );

  const { control, handleSubmit, setValue, watch, reset } = useForm(formConfig);

  // Mobile duplicate check - get mobile from form values
  const formValues = watch();
  const mobile = formValues?.mobile || formValues?.phone || formValues?.Mobile;
  const calling_code =
    formValues?.calling_code ||
    formValues?.country_code ||
    formValues?.CallingCode;

  // Send only the mobile number (without calling code) for duplicate check
  const mobileForCheck = mobile ? mobile.replace(/\D/g, "") : null; // Remove all non-digits

  const { checkMobile: isMobileTaken, loading: mobileCheckLoading } =
    useCheckMobile(
      featuresList?.includes("NoMobileDuplicate") ? mobileForCheck : null,
      featuresList?.includes("NoMobileDuplicate") ? link : null,
    );

  const handleClose = () => {
    setOpen(false);
    (errorInPaymentInitialization || isPopupNotOpened) && reset();
    errorInPaymentInitialization && setErrorInPaymentInitialization(false);
    isPopupNotOpened && setIsPopupNotOpened(false);
    errorInPaymentInitialization && setErrorInPaymentInitialization(false);
    isPopupNotOpened && setIsPopupNotOpened(false);
  };

  React.useEffect(() => {
    if (errorInPaymentInitialization) {
      handleClose();
      setEmailError("Something went wrong please Try Again Later!");
    }
    if (isPopupNotOpened) {
      handleClose();
      setEmailError(
        "Please allow pop-ups for this website to proceed with the payment. \n In the address bar, click the Pop-up blocked icon 🔒. then select always allow popups",
      );
    }
    // if (!selectedPaymentOption || !allData?.paymentMethod) {
    //   setEmailError("Please Select Payment Method")
    // }
  }, [errorInPaymentInitialization, isPopupNotOpened]);
  useEffect(() => {
    // Extract numeric value from payment defaultValue
    const paymentField = fields.find((f) => f.name === "payment");
    if (paymentField) {
      const match = paymentField.defaultValue.match(/\d+/);
      const amount = match ? parseFloat(match[0]) : 0;
      setOriginalPrice(amount);
      setPrice(amount);
    }
  }, [fields]);
  const [doRegister, { loading: registerLoading, error: registerError }] =
    useMutation(REGISTER);
  // Memoize country code lookup function to prevent re-creation on every render
  const findCountryCodeByDialCode = React.useCallback(
    (dialCode) => {
      const country = countries.find((c) => c.dial_code === dialCode);
      return !selectedCountry ? country?.label : "Oman";
    },
    [selectedCountry],
  );
  const handleMemberIdValidation = () => {
    if (!memberIdValue) {
      setMemberIdError("");
      return;
    }
    if (isMemberIdValid) {
      // Clear both value and discount
      setMemberIdValue("");
      setIsMemberIdValid(false);
      setMemberDiscount(0);
      setMemberIdError("");
    } else {
      checkMemberId({
        variables: { memberId: memberIdValue, link },
        onCompleted: (data) => {
          if (data.checkMemberId.isValid) {
            setIsMemberIdValid(true);
            setMemberIdError("");
            // Set discount from API response
            setMemberDiscount(parseFloat(data.checkMemberId.discount) || 0);
          } else {
            setIsMemberIdValid(false);
            setMemberIdError(data.checkMemberId.message);
          }
        },
      });
    }
  };

  // const handlePromoCodeValidation = () => {
  //   if (promoStatus === "valid") {
  //     // Clear both value and discount
  //     setPromoCode("");
  //     setPromoDiscount(0);
  //     setPromoStatus(null);
  //     setPromoMessage("");
  //   } else {
  //     validatePromo({
  //       variables: { code: promoCode, link },
  //       onCompleted: (data) => {
  //         if (data.validatePromo.status) {
  //           setPromoStatus("valid");
  //           setPromoMessage(
  //             `${data.validatePromo.discountPercent}% discount applied!`
  //           );
  //           setPromoDiscount(data.validatePromo.discountPercent);
  //         } else {
  //           setPromoStatus("invalid");
  //           setPromoMessage(data.validatePromo.message);
  //         }
  //       },
  //     });
  //   }
  // };
  // const handlePaymentSubmit = async (e) => {
  //   try {
  //     // Extract numeric value from payment string
  //     const amountMatch = e.payment.match(/\d+(\.\d+)?/);
  //     const baseAmount = amountMatch ? parseFloat(amountMatch[0]) : 0;

  //     // Apply discounts
  //     let finalAmount = baseAmount;

  //     // Apply member discount (fixed amount)
  //     if (isMemberIdValid) {
  //       finalAmount -= memberDiscount;
  //     }

  //     // Apply promo discount (percentage)
  //     if (promoDiscount > 0) {
  //       finalAmount *= 1 - promoDiscount / 100;
  //     }

  //     console.log("Final payable amount:", finalAmount);

  //     // Rest of payment processing logic
  //     let matchingPaymentMethodData = paymentData?.[0];
  //     if (paymentData?.length > 1) {
  //       matchingPaymentMethodData = paymentData.find(
  //         (data) => data.paymentMethod === e.paymentMethod
  //       );
  //     }

  //     const { apiKey, apiurl, secretKey, publicKey, currency } =
  //       matchingPaymentMethodData || {};

  //     // Validation checks
  //     if (featuresList.includes("MemberCheck") && !isMemberIdValid) {
  //       setMemberIdError("Valid Member ID required");
  //       return;
  //     }

  //     if (promoCode && promoStatus !== "valid") {
  //       setPromoStatus("invalid Code");
  //       return;
  //     }

  //     // Update payment state
  //     setAllData(e);
  //     setPrice(finalAmount);
  //     setSelectedCurrency(currency);
  //     setClientId(apiKey);
  //     setApiurl(apiurl);
  //     setSecretKey(secretKey);
  //     setPublicKey(publicKey);
  //     handleClickOpen();
  //   } catch (error) {
  //     console.error("Payment processing failed:", error);
  //     setEmailError("Error processing payment. Please try again.");
  //   }
  // };

  React.useEffect(() => {
    let timer;
    if (paymentFailedLoading) {
      timer = setTimeout(() => {
        setPaymentFailedLoading(false);
        window.location.reload();
      }, 7000); // 7 seconds
    }

    return () => clearTimeout(timer);
  }, [paymentFailedLoading]);

  if (paymentFailedLoading) {
    return (
      <StatusScreen
        variant="error"
        title="Payment Failed"
        message="Your payment didn't go through. No amount was charged — please try again."
        icon={<GppBadIcon />}
        autoReloadSeconds={7}
        actions={[{ label: "Try Again", primary: true, onClick: () => window.location.reload() }]}
        eventId={eventId}
      />
    );
  }

  if (PaymentRegisterLoading)
    return (
      <StatusScreen
        variant="loading"
        title="Processing your registration"
        message="Please wait while we confirm your payment and complete your registration. Don't close this window."
        eventId={eventId}
      />
    );

  return (
    <>
      {/* <ScreenSizeDebugger show={true} /> */}
      <SearchModal
        eventId={eventId}
        role={role}
        open={openModal}
        onClose={handleModalClose}
        featuresList={featuresList}
        customTheme={customTheme}
      />
      <OtpModal {...otpModalProps} />
      {featuresList.includes("DownloadBadge") &&
        (() => {
          const languageDisplay = getLanguageDisplay(featuresList);
          return (
            <Stack
              direction="row"
              justifyContent={"center"}
              spacing={2}
              alignItems="center"
              p={2}
            >
              <Typography variant="body1" textAlign={"center"}>
                {languageDisplay.showArabic && (
                  <span style={{ direction: "rtl" }}>
                    إذا قمت بالتسجيل مسبقًا،{" "}
                    <Typography
                      variant="caption"
                      onClick={handleModalOpen}
                      sx={{
                        color: parsedTheme?.primary || "blueviolet",
                        fontSize: 18,
                        cursor: "pointer",
                        "&:hover": {
                          color: parsedTheme?.secondary || "purple",
                        },
                      }}
                    >
                      اضغط هنا{" "}
                    </Typography>
                    لاسترجاع بطاقة الدخول.
                  </span>
                )}
                {languageDisplay.showArabic && languageDisplay.showEnglish && (
                  <br />
                )}
                {languageDisplay.showEnglish && (
                  <span style={{ direction: "ltr" }}>
                    If you have previously registered,{" "}
                    <Typography
                      variant="caption"
                      onClick={handleModalOpen}
                      sx={{
                        color: parsedTheme?.primary || "blueviolet",
                        fontSize: 18,
                        cursor: "pointer",
                        "&:hover": {
                          color: parsedTheme?.secondary || "purple",
                        },
                      }}
                    >
                      click here{" "}
                    </Typography>
                    to retrieve your badge.
                  </span>
                )}
              </Typography>
            </Stack>
          );
        })()}

      <form
        noValidate
        onSubmit={handleSubmit(async (f) => {
          if (
            (memberIdValue && !isMemberIdValid) ||
            (chkmid && !memberIdValue)
          ) {
            setMemberIdError("Please validate your Member ID");
            return;
          }

          // Check if promo code is present but invalid
          if (promoCode && promoStatus !== "valid") {
            setPromoMessage("Please enter a valid promo code");
            return;
          }
          // Duplicate-email guard. Unless duplicates are explicitly allowed, block submit both when the
          // live check already flagged the email AND while that check is still in flight — closing the race
          // where a fast submit slips past the debounce and only the backend catches it (the "only after
          // submit" complaint). The submit button is also disabled in these states (see LoadingButton).
          if (!featuresList.includes("duplicateEmailAllowed")) {
            if (emailTakenHit) {
              return;
            }
            if (emailCheckLoading) {
              // Still verifying — don't fire register yet; the inline "Checking email…" stays visible.
              return;
            }
          }

          // Check mobile duplicate
          if (
            isMobileTaken === "Mobile number already exists" &&
            featuresList.includes("NoMobileDuplicate")
          ) {
            // Prevent form submission
            return;
          }
          const v = {};
          for (const r of fields) {
            if (r.options?.some((op) => op === "$Other")) {
              if (["select", "radio", "payment"].includes(r.type)) {
                if (f[r.name] === "$Other") {
                  v[r.name] = "Other: " + (f[r.name + "_other"] || "");
                  continue;
                }
              } else if (typeof f[r.name] === "object") {
                if (f[r.name]?.includes("$Other")) {
                  v[r.name] = [
                    ...(f[r.name]?.filter((o) => o !== "$Other") || []),
                    "Other: " + (f[r.name + "_other"] || ""),
                  ]
                    .filter((a) => !!a?.trim())
                    .join(", ");
                  continue;
                } else {
                  v[r.name] = (f[r.name] || [])
                    .filter((a) => !!a?.trim())
                    .join(", ");
                  continue;
                }
              }
            } else if (typeof f[r.name] === "object") {
              v[r.name] = f[r.name].join(", ");
            } else {
              v[r.name] = f[r.name];
            }
          }

          // Add referral data if present
          const finalFormData = prepareReferralFormData(v, referralParams);

          // Remember the email this registration used, so the post-registration Share Generator can
          // auto-fill the sharer's own details via ?prefill_email=<email>.
          const registeredEmail =
            finalFormData?.email || finalFormData?.Email || email || "";
          if (registeredEmail && onRegisteredEmail) onRegisteredEmail(registeredEmail);

          if (featuresList.includes("Captcha") && !captchaOff) {
            if (!token) {
              setEmailError("Captcha Required");
              return;
            }
            setEmailError(null);
          } else {
            setEmailError(null);
          }

          let wholeData = {};
          const isPmtTrue = pmt === null ? true : pmt?.amount > 0;

          try {
            if (featuresList.includes("PaymentEnable") && isPmtTrue) {
              if (paymentData?.length >= 1) {
                // Create payment data with discounts applied
                wholeData = {
                  paymentMethod:
                    paymentData.length > 1
                      ? f.paymentMethod
                      : paymentData[0].paymentMethod,
                  ...finalFormData,
                };

                // Extract and process amount with discounts
                const amountMatch = wholeData.payment.match(/\d+(\.\d+)?/);
                let amount = amountMatch ? parseFloat(amountMatch[0]) : 0;

                // Apply discounts
                if (isMemberIdValid) amount -= memberDiscount;
                // Promo is EITHER a percentage OR a flat amount (never both). Flat is a
                // fixed amount off, clamped so the total never goes negative.
                if (promoDiscount > 0) amount *= 1 - promoDiscount / 100;
                if (promoFlat > 0) amount = Math.max(0, amount - promoFlat);

                // Set final price
                setPrice(amount);

                // Process payment method
                const methodData =
                  paymentData.find(
                    (data) => data.paymentMethod === wholeData.paymentMethod,
                  ) || paymentData[0];

                setAllData(wholeData);
                setSelectedCurrency(methodData.currency);
                setClientId(methodData.apiKey);
                setApiurl(methodData.apiurl);
                setSecretKey(methodData.secretKey);
                setPublicKey(methodData.publicKey);

                // Check for OTP requirements before opening payment modal
                const email = wholeData?.email || wholeData?.Email;
                const mobile =
                  wholeData?.mobile || wholeData?.phone || wholeData?.Mobile;
                const calling_code =
                  wholeData?.calling_code ||
                  wholeData?.country_code ||
                  wholeData?.CallingCode;
                const updatedMobile =
                  mobile && calling_code
                    ? `${calling_code.replace("+", "")}${mobile}`
                    : null;

                const isMobileOTPRequired =
                  featuresList?.includes("MobileOTP") ||
                  featuresList?.includes("mobileOTP") ||
                  featuresList?.includes("mobile_otp");
                const isEmailOTPRequired =
                  featuresList?.includes("EmailOTP") ||
                  featuresList?.includes("emailOTP") ||
                  featuresList?.includes("email_otp");
                const isLegacyOTPRequired = featuresList?.includes("OTP");

                if (
                  isMobileOTPRequired ||
                  isEmailOTPRequired ||
                  isLegacyOTPRequired
                ) {
                  let finalMobileOTP = isMobileOTPRequired;
                  let finalEmailOTP = isEmailOTPRequired;

                  if (isLegacyOTPRequired) {
                    if (updatedMobile) {
                      finalMobileOTP = true;
                    } else if (email) {
                      finalEmailOTP = true;
                    }
                  }

                  // Validate required data for OTP
                  if (finalMobileOTP && !updatedMobile) {
                    console.error(
                      "Mobile OTP required but no mobile number provided",
                    );
                    return;
                  }
                  if (finalEmailOTP && !email) {
                    console.error("Email OTP required but no email provided");
                    return;
                  }

                  // Show OTP Modal and wait for verification
                  const otpVerified = await new Promise((resolve) => {
                    setOtpModalProps({
                      isOpen: true,
                      mobile: finalMobileOTP ? updatedMobile : null,
                      email: finalEmailOTP ? email : null,
                      eventId: eventId,
                      role: role,
                      onVerify: (success) => {
                        setOtpModalProps({ ...otpModalProps, isOpen: false });
                        resolve(success);
                      },
                    });
                  });

                  if (!otpVerified) {
                    console.log("OTP verification failed.");
                    return; // Stop further execution if OTP verification fails
                  }
                }

                handleClickOpen();
              }
            } else {
              // Handle free registration
              let variable;

              if (
                featuresList?.includes("MemberCheck") &&
                featuresList?.includes("emailVerification")
              ) {
                // console.log("11111111 ");
                if (chkmid && iplink) {
                  // console.log("all true run");
                  variable = {
                    link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                    jsonData: JSON.stringify({
                      ...finalFormData,
                      memberId: memberIdValue,
                      status: "accept",
                    }),
                  };
                } else if (chkmid) {
                  console.log("chkmid true run");
                  variable = {
                    link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                    jsonData: JSON.stringify({
                      ...finalFormData,
                      memberId: memberIdValue,
                      status: "accept",
                    }),
                  };
                } else if (iplink) {
                  console.log("iplink true run");
                  variable = {
                    link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                    jsonData: JSON.stringify({
                      ...finalFormData,
                      status: "accept",
                    }),
                  };
                } else {
                  console.log("all false run");
                  variable = {
                    link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                    jsonData: JSON.stringify(finalFormData),
                  };
                }
              } else if (
                featuresList?.includes("emailVerification") &&
                iplink
              ) {
                console.log("emailVerification and iplink true run");
                variable = {
                  link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                  jsonData: JSON.stringify({
                    ...finalFormData,
                    status: "accept",
                  }),
                };
              } else {
                console.log("default case run");
                variable = {
                  link: JSON.stringify({ link, pmtLink: pmt?.param, ...(extractRegLinkSource() ? { source: extractRegLinkSource() } : {}) }),
                  jsonData: JSON.stringify(finalFormData),
                };
              }

              // Check for OTP requirements before free registration
              const email = finalFormData?.email || finalFormData?.Email;
              const mobile =
                finalFormData?.mobile ||
                finalFormData?.phone ||
                finalFormData?.Mobile;
              const calling_code =
                finalFormData?.calling_code ||
                finalFormData?.country_code ||
                finalFormData?.CallingCode;
              const updatedMobile =
                mobile && calling_code
                  ? `${calling_code.replace("+", "")}${mobile}`
                  : null;

              const isMobileOTPRequired =
                featuresList?.includes("MobileOTP") ||
                featuresList?.includes("mobileOTP") ||
                featuresList?.includes("mobile_otp");
              const isEmailOTPRequired =
                featuresList?.includes("EmailOTP") ||
                featuresList?.includes("emailOTP") ||
                featuresList?.includes("email_otp");
              const isLegacyOTPRequired = featuresList?.includes("OTP");

              if (
                isMobileOTPRequired ||
                isEmailOTPRequired ||
                isLegacyOTPRequired
              ) {
                let finalMobileOTP = isMobileOTPRequired;
                let finalEmailOTP = isEmailOTPRequired;

                if (isLegacyOTPRequired) {
                  if (updatedMobile) {
                    finalMobileOTP = true;
                  } else if (email) {
                    finalEmailOTP = true;
                  }
                }

                // Validate required data for OTP
                if (finalMobileOTP && !updatedMobile) {
                  console.error(
                    "Mobile OTP required but no mobile number provided",
                  );
                  return;
                }
                if (finalEmailOTP && !email) {
                  console.error("Email OTP required but no email provided");
                  return;
                }

                // Show OTP Modal and wait for verification
                const otpVerified = await new Promise((resolve) => {
                  setOtpModalProps({
                    isOpen: true,
                    mobile: finalMobileOTP ? updatedMobile : null,
                    email: finalEmailOTP ? email : null,
                    eventId: eventId,
                    role: role,
                    onVerify: (success) => {
                      setOtpModalProps({ ...otpModalProps, isOpen: false });
                      resolve(success);
                    },
                  });
                });

                if (!otpVerified) {
                  console.log("OTP verification failed.");
                  return; // Stop further execution if OTP verification fails
                }
              }

              const result = await doRegister({
                variables: variable,
              });

              if (result) onSubmit(result.data?.register);
            }
          } catch (error) {
            console.error("Registration error:", error);
            // If the backend rejected because the email/mobile is already registered, show ONE clean
            // "already registered" line — NOT the generic "Registration failed" (which the duplicate case
            // would otherwise double up with). Any other failure keeps the generic message.
            const raw =
              error?.graphQLErrors?.[0]?.message || error?.message || "";
            const msg = String(raw).toLowerCase();
            const isDuplicate =
              msg.includes("already registered") ||
              msg.includes("already exists") ||
              msg.includes("already have") ||
              msg.includes("duplicate");
            if (isDuplicate) {
              // ONE clean toast — no red line above the button. Use the backend's specific message
              // if it names a role ("…under 'visitor'…"), else a clean default.
              setEmailError("");
              setDupToast(
                /already registered|already exists/i.test(raw)
                  ? String(raw)
                  : "This email is already registered.",
              );
            } else {
              setEmailError("Registration failed. Please check your details.");
            }
          }
        })}
      >
        <Grid container spacing={2}>
          {memoizedFields?.map((f) => (
            <React.Fragment key={f.name}>
              <Grid
                item
                md={isMobile ? f?.xs || 12 : f?.md || 6}
                xs={isMobile ? f?.xs || 12 : f?.md || 6}
              >
                <Controller
                  name={f.name}
                  control={control}
                  render={(rhf) => (
                    <FormField
                      rhf={rhf}
                      f={f}
                      setValue={setValue}
                      findCountryCodeByDialCode={findCountryCodeByDialCode}
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry}
                      selectedMobileCode={selectedMobileCode}
                      setSelectedMobileCode={setSelectedMobileCode}
                      isMobileTaken={isMobileTaken}
                      mobileCheckLoading={mobileCheckLoading}
                      parsedRegistration={parsedRegistration}
                      pmt={pmt}
                      setEmail={setEmail}
                      isEmailTaken={isEmailTaken}
                      emailCheckLoading={emailCheckLoading}
                      showFamilyMembers={showFamilyMembers}
                      setOriginalPrice={setOriginalPrice}
                      setPrice={setPrice}
                      promoDiscount={promoDiscount}
                      promoFlat={promoFlat}
                      memberDiscount={memberDiscount}
                      isMemberIdValid={isMemberIdValid}
                      customTheme={customTheme}
                      featuresList={featuresList}
                      prefilledEmail={prefilledEmail}
                      socialProvider={socialProvider}
                      uid={uid}
                      otherFieldState={getOtherFieldState(f.name)}
                      updateOtherFieldState={(updates) =>
                        updateOtherFieldState(f.name, updates)
                      }
                    />
                  )}
                />
              </Grid>
              {f.options?.some((op) => op === "$Other") &&
                watch(f.name)?.includes("$Other") && (
                  <Grid item xs={f.xs} md={f.md}>
                    <Controller
                      name={f.name + "_other"}
                      control={control}
                      rules={{
                        required: f.required ? "Required" : false,
                        maxLength: f.maxLength,
                        minLength: f.minLength,
                        // pattern: f.pattern
                      }}
                      render={(rhf) => (
                        <FormField
                          rhf={rhf}
                          parsedRegistration={parsedRegistration}
                          f={{
                            type: "text",
                            name: "Enter value",
                            placeholder: "Custom value",
                            required: f.required,
                            sx: {},
                          }}
                          selectedCountry={selectedCountry}
                          setSelectedCountry={setSelectedCountry}
                          setValue={setValue}
                          findCountryCodeByDialCode={findCountryCodeByDialCode}
                          selectedMobileCode={selectedMobileCode}
                          setSelectedMobileCode={setSelectedMobileCode}
                          pmt={pmt}
                          setEmail={setEmail}
                          isEmailTaken={isEmailTaken}
                          emailCheckLoading={emailCheckLoading}
                          showFamilyMembers={showFamilyMembers}
                          setOriginalPrice={setOriginalPrice}
                          setPrice={setPrice}
                          promoDiscount={promoDiscount}
                          promoFlat={promoFlat}
                          memberDiscount={memberDiscount}
                          isMemberIdValid={isMemberIdValid}
                          featuresList={featuresList}
                          uid={uid}
                        />
                      )}
                    />
                  </Grid>
                )}
            </React.Fragment>
          ))}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {featuresList.includes("MemberCheck") && chkmid && (
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Member ID"
                  fullWidth
                  value={memberIdValue}
                  // onChange={(e) => setMemberIdValue(e.target.value)}
                  onChange={(e) => {
                    setMemberIdValue(e.target.value);
                    // Clear error when input changes
                    if (!e.target.value) setMemberIdError("");
                    else if (isMemberIdValid) {
                      setIsMemberIdValid(false);
                      setMemberIdError("");
                    }
                  }}
                  error={!!memberIdError}
                  helperText={memberIdError}
                />
                <Button
                  variant="contained"
                  onClick={handleMemberIdValidation}
                  disabled={!memberIdValue}
                  color={isMemberIdValid ? "error" : "primary"}
                >
                  {isMemberIdValid ? "Remove" : "Validate"}
                </Button>
              </Stack>
              {isMemberIdValid && (
                <Typography color="success.main" variant="body2">
                  <FiCheckCircle />
                  Valid member ID
                  {(!pmt || pmt?.amount !== 0) &&
                    featuresList?.includes("PaymentEnable") && (
                      <>-{memberDiscount.toFixed(2)} OMR discount</>
                    )}
                </Typography>
              )}
            </Grid>
          )}
          {featuresList.includes("PaymentEnable") &&
            featuresList.includes("PromoCode") &&
            (pmt && pmt?.amount === 0 ? (
              ""
            ) : (
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value);
                      if (!e.target.value) {
                        setPromoDiscount(0);
                        setPromoFlat(0);
                        setPromoStatus(null);
                        setPromoMessage("");
                      }
                    }}
                    label="Promo Code"
                    fullWidth
                    error={promoStatus === "invalid"}
                    helperText={promoMessage}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (promoStatus === "valid") {
                        setPromoDiscount(0);
                        setPromoFlat(0);
                        setPromoStatus(null);
                        setPromoMessage("");
                      } else {
                        validatePromo({
                          // Pass the registrant email so individual (fixed-email) coupons
                          // can enforce their allow-list; empty is fine for other coupons.
                          variables: {
                            code: promoCode,
                            link,
                            email: allData?.email || allData?.Email || null,
                          },
                          onCompleted: (data) => {
                            if (data.validatePromo.status) {
                              // The backend returns exactly ONE of discountPercent / discountValue
                              // (the other is null). Apply percentage OR flat accordingly.
                              const pct = data.validatePromo.discountPercent;
                              const flat = data.validatePromo.discountValue;
                              setPromoStatus("valid");
                              if (pct) {
                                setPromoDiscount(pct);
                                setPromoFlat(0);
                                setPromoMessage(`${pct}% discount applied!`);
                              } else if (flat) {
                                setPromoFlat(flat);
                                setPromoDiscount(0);
                                setPromoMessage(`${flat} discount applied!`);
                              } else {
                                setPromoDiscount(0);
                                setPromoFlat(0);
                                setPromoMessage(
                                  data.validatePromo.message ||
                                    "Promo code applied.",
                                );
                              }
                            } else {
                              setPromoStatus("invalid");
                              setPromoDiscount(0);
                              setPromoFlat(0);
                              setPromoMessage(data.validatePromo.message);
                            }
                          },
                        });
                      }
                    }}
                    disabled={!promoCode}
                    color={promoStatus === "valid" ? "error" : "primary"}
                  >
                    {promoStatus === "valid" ? "Remove" : "Apply"}
                  </Button>
                </Stack>
              </Grid>
            ))}

          <Grid item xs={12}>
            <Divider />
          </Grid>
          {/* Owner 2026-08-06: a duplicate email must surface ONLY as the toast — the submit catch
              already fires it, but Apollo's own `registerError` state ALSO rendered the same message
              as this red line (the double display in the screenshot). Suppress the red line for
              duplicate-type messages; every other register failure renders here unchanged. */}
          {registerError &&
            !/already registered|already exists|already have|duplicate/i.test(
              registerError?.message || "",
            ) && (
            <Grid item xs={12}>
              <Typography color="error">{registerError?.message}</Typography>
            </Grid>
          )}
          {registerLoading && (
            <Grid item xs={12}>
              <LinearProgress />
            </Grid>
          )}
          {pmt && pmt?.amount === 0
            ? null
            : paymentData?.length > 1 &&
              featuresList.includes("PaymentEnable") && (
                <Grid item xs={12}>
                  <Controller
                    name="paymentMethod"
                    control={control}
                    defaultValue={paymentData[0]?.paymentMethod}
                    rules={{ required: "Please select a payment method" }}
                    render={({ field }) => (
                      <FormControl>
                        <FormLabel id="demo-row-radio-buttons-group-label">
                          Select Payment Option
                        </FormLabel>
                        <RadioGroup {...field}>
                          <Stack direction="row" spacing={2}>
                            {paymentData.map((item, i) => (
                              <CustomRadioButton
                                key={i}
                                item={item}
                                setSelectedPaymentOption={(method) => {
                                  setSelectedPaymentOption(method);
                                  // Update currency when payment method changes
                                  const selected = paymentData.find(
                                    (p) => p.paymentMethod === method,
                                  );
                                  setSelectedCurrency(
                                    selected?.currency || "OMR",
                                  );
                                }}
                              />
                            ))}
                          </Stack>
                        </RadioGroup>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}

          <Grid item xs={12}>
            <Stack
              direction="column"
              justifyContent={"center"}
              spacing={2}
              alignItems="center"
            >
              {featuresList.includes("GroupBookingEnable") && ( // Check if GroupBookingEnable feature is enabled
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showFamilyMembers}
                      onChange={(e) => setShowFamilyMembers(e.target.checked)} // Update state on checkbox change
                    />
                  }
                  label="Add Family Members"
                />
              )}
              {featuresList.includes("Captcha") && !captchaOff && (
                <Turnstile
                  // sitekey="1x00000000000000000000AA"
                  sitekey="0x4AAAAAAAXSYmh0wYoO56yA"
                  onVerify={(token) => {
                    setToken(token);

                    // console.log("Token>>>>>>>>>",token);
                    // fetch("/login", {
                    //   method: "POST",
                    //   body: JSON.stringify({ token }),
                    // }).then((response) => {
                    //   if (!response.ok) turnstile.reset();
                    // });
                  }}
                />
              )}
              {featuresList.includes("PaymentEnable") &&
                (pmt && pmt?.amount === 0 ? (
                  ""
                ) : (
                  <Grid item xs={12}>
                    <Paper
                      elevation={3}
                      sx={{
                        p: 3,
                        mb: 3,
                        borderLeft: 4,
                        borderColor: "primary.main",
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        {/* <AttachMoney
                        color="primary"
                        sx={{ mr: 1, fontSize: 32 }}
                      /> */}
                        <Typography
                          variant="h5"
                          component="div"
                          fontWeight="bold"
                        >
                          Payment Summary
                        </Typography>
                      </Box>

                      <Stack spacing={1.5}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography variant="body1" color="text.secondary">
                            Original Amount:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {originalPrice.toFixed(2)} {selectedCurrency}
                          </Typography>
                        </Box>

                        {memberDiscount > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Discount
                                fontSize="small"
                                color="success"
                                sx={{ mr: 0.5 }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Member Discount:
                              </Typography>
                            </Box>
                            <Typography
                              variant="body1"
                              color="success.main"
                              fontWeight="medium"
                            >
                              -{memberDiscount.toFixed(2)} {selectedCurrency}
                            </Typography>
                          </Box>
                        )}

                        {promoDiscount > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <LocalOffer
                                fontSize="small"
                                color="success"
                                sx={{ mr: 0.5 }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Promo Discount ({promoDiscount}%):
                              </Typography>
                            </Box>
                            <Typography
                              variant="body1"
                              color="success.main"
                              fontWeight="medium"
                            >
                              -
                              {(
                                (originalPrice - memberDiscount) *
                                (promoDiscount / 100)
                              ).toFixed(2)}{" "}
                              {selectedCurrency}
                            </Typography>
                          </Box>
                        )}

                        {promoFlat > 0 && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <LocalOffer
                                fontSize="small"
                                color="success"
                                sx={{ mr: 0.5 }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                              >
                                Promo Discount:
                              </Typography>
                            </Box>
                            <Typography
                              variant="body1"
                              color="success.main"
                              fontWeight="medium"
                            >
                              -
                              {Math.min(
                                promoFlat,
                                originalPrice - memberDiscount,
                              ).toFixed(2)}{" "}
                              {selectedCurrency}
                            </Typography>
                          </Box>
                        )}

                        <Divider sx={{ my: 1.5 }} />

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            backgroundColor: (theme) => theme.palette.grey[100],
                            p: 1.5,
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            Total Payable:
                          </Typography>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="primary"
                          >
                            {Math.max(
                              0,
                              (originalPrice - memberDiscount) *
                                (1 - promoDiscount / 100) -
                                promoFlat,
                            ).toFixed(2)}{" "}
                            {selectedCurrency}
                          </Typography>
                        </Box>

                        {promoDiscount > 0 && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 1,
                            }}
                          >
                            <Info fontSize="small" sx={{ mr: 0.5 }} />
                            Discounts applied in sequence: Member discount
                            first, then promo code
                          </Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              {emailError && (
                <Typography color="error">
                  <div dangerouslySetInnerHTML={{ __html: emailError }}></div>
                </Typography>
              )}
              <LoadingButton
                loading={registerLoading}
                type="submit"
                size="large"
                variant="contained"
                // Owner 2026-08-05: DISABLE submit on a duplicate email (or mobile) so it's visually clear
                // the form can't be sent — instead of letting the user click and get "Registration failed".
                // Same conditions the submit handler already guards on (honors duplicateEmailAllowed /
                // NoMobileDuplicate). Also disabled while the async check is still running.
                disabled={
                  emailCheckLoading ||
                  (emailTakenHit && !featuresList.includes("duplicateEmailAllowed")) ||
                  (isMobileTaken === "Mobile number already exists" &&
                    featuresList.includes("NoMobileDuplicate"))
                }
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 1,
                  flexDirection:
                    featuresList.includes("arabic") &&
                    !featuresList.includes("english")
                      ? "row"
                      : featuresList.includes("english") &&
                          !featuresList.includes("arabic")
                        ? "row"
                        : "row-reverse", // Default: Arabic first, then English
                  backgroundColor: customTheme?.palette?.primary?.main,
                  "&:hover": {
                    backgroundColor:
                      customTheme?.palette?.primary?.dark ||
                      customTheme?.palette?.secondary?.main,
                  },
                  "&:disabled": {
                    backgroundColor: customTheme?.palette?.action?.disabled,
                  },
                }}
              >
                {/* Show Arabic text if arabic feature is enabled and not english-only */}
                {featuresList.includes("arabic") &&
                  !featuresList.includes("english") && (
                    <Typography component="span" sx={{ direction: "rtl" }}>
                      إرسال
                    </Typography>
                  )}

                {/* Show both Arabic and English if both are enabled or default */}
                {(!featuresList.includes("english") &&
                  !featuresList.includes("arabic")) ||
                featuresList.includes("english-arabic") ||
                (featuresList.includes("english") &&
                  featuresList.includes("arabic")) ? (
                  <>
                    <Typography component="span" sx={{ direction: "rtl" }}>
                      إرسال
                    </Typography>
                    <AppRegistrationIcon />
                    <Typography component="span" sx={{ direction: "ltr" }}>
                      Submit
                    </Typography>
                  </>
                ) : (
                  <>
                    <AppRegistrationIcon />
                    {/* Show English text if english feature is enabled and not arabic-only */}
                    {featuresList.includes("english") &&
                      !featuresList.includes("arabic") && (
                        <Typography component="span" sx={{ direction: "ltr" }}>
                          Submit
                        </Typography>
                      )}
                  </>
                )}
              </LoadingButton>
            </Stack>

            <PaymentSummaryDialog
              open={open}
              handleClose={handleClose}
              price={price}
              selectedCurrency={selectedCurrency}
              clientId={clientId}
              secretKey={secretKey}
              publicKey={publicKey}
              handlePaymentSuccess={handlePaymentSuccess}
              allData={allData}
              setErrorInPaymentInitialization={setErrorInPaymentInitialization}
              selectedPaymentOption={selectedPaymentOption}
              base={apiurl}
              eventId={eventId}
              role={role}
              setIsPopupNotOpened={setIsPopupNotOpened}
              link={link}
            />
          </Grid>
        </Grid>
      </form>

      {/* Duplicate-email TOAST — shows at the bottom, NOT as a red line above the submit button.
          Owner 2026-09-12: does NOT auto-close — the user must see why the submit button is
          disabled for as long as the duplicate stands. It clears automatically when the email is
          no longer a duplicate (see the emailTakenHit effect) or when dismissed by hand.
          `onClose` ignores clickaway so a stray click on the page can't hide it. */}
      <Snackbar
        open={!!dupToast}
        autoHideDuration={null}
        onClose={(_e, reason) => {
          if (reason === "clickaway") return;
          setDupToast("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setDupToast("")} severity="warning" sx={{ width: "100%" }}>
          {dupToast}
        </Alert>
      </Snackbar>
    </>
  );
}
